/**
 * FoodItemPage — full 22-section public profile for a food item.
 *
 * Route: /food-pulse/food/:slug
 *
 * The 22 sections are rendered as an anchor-linked table of contents
 * followed by expandable / collapsible cards so the page stays
 * scannable. Everything is null-tolerant — sections whose data the AI
 * couldn't fill are hidden.
 */
import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft, ExternalLink, Sparkles, Leaf, Utensils,
  QrCode, ScanLine, AlertTriangle, ShieldCheck, Award,
  Copy, Check, Heart, ShoppingCart, Home as HomeIcon, HandHeart,
  BookOpen, Star, CalendarDays, Sprout, Newspaper,
  Share2, Package, Gift, Store,
} from "lucide-react";
import SeoHead from "@/components/SeoHead";
import NearbyPricesBlock from "@/components/NearbyPricesBlock";
import FarmersPostingStrip from "@/components/FarmersPostingStrip";
import { http } from "@/lib/api";
import { normalizeShortUrl, normalizeAssetUrl, synthesizeQrFromShortUrl } from "@/lib/splinkUrl";

const API = process.env.REACT_APP_BACKEND_URL;

const SECTION_MAP = [
  ["basic",      "Basic Information"],
  ["names",      "Alternate Names"],
  ["images",     "Gallery"],
  ["health",     "Health Benefits"],
  ["nutrition",  "Nutrition"],
  ["consumption","Consumption"],
  ["cultivation","Cultivation"],
  ["production", "Global Production"],
  ["harvest",    "Harvesting"],
  ["byproducts", "By-products"],
  ["storage",    "Storage"],
  ["recipes",    "Recipes"],
  ["safety",     "Food Safety"],
  ["environment","Environmental Impact"],
  ["economic",   "Economic Info"],
  ["culture",    "Cultural Importance"],
  ["facts",      "Interesting Facts"],
  ["faqs",       "FAQs"],
  ["refs",       "References"],
  ["links",      "Website Links"],
  ["videos",     "Videos"],
  ["meta",       "Related Foods"],
];

const Section = ({ id, title, children }) => (
  <section id={id} className="scroll-mt-24 rounded-2xl border p-5 sm:p-6 bg-white" style={{ borderColor: "#B8D3A1" }} data-testid={`food-section-${id}`}>
    <h2 className="font-display text-2xl mb-3" style={{ color: "#1A3B1A" }}>{title}</h2>
    <div className="text-sm leading-relaxed" style={{ color: "#3E5A3E" }}>{children}</div>
  </section>
);

const KV = ({ label, value }) => (value === undefined || value === null || value === "" ? null : (
  <div className="flex flex-col">
    <span className="text-[10px] font-mono uppercase tracking-widest opacity-60">{label}</span>
    <span className="text-sm mt-0.5">{String(value)}</span>
  </div>
));

function pill(text, i) {
  return <span key={i} className="text-[11px] font-mono px-2 py-0.5 rounded-full mr-1 mb-1 inline-block" style={{ background: "#DCEAD1", color: "#2F6B2F" }}>{text}</span>;
}

// ── ProductActionsCard — 8 "Add to My Lists" + 4 "Share or Save This Item"
// buttons rendered as a 2-column card matching the mobile mockup. Each
// list-button routes to /food-pulse/lists/wishlist/new with a `mirror`
// hint so the unified add-form pre-toggles the target list. Share
// buttons route to the existing share/spot/donation/store flows.
// ------------------------------------------------------------------
const LIST_ACTIONS = [
  { key: "wish",         label: "Wish List",      Icon: Heart },
  { key: "shopping",     label: "Shopping List",  Icon: ShoppingCart },
  { key: "pantry",       label: "Pantry List",    Icon: HomeIcon },
  { key: "donation",     label: "Donation List",  Icon: HandHeart },
  { key: "recipe_ideas", label: "Recipe Ideas",   Icon: BookOpen },
  { key: "favorites",    label: "Favorites",      Icon: Star },
  { key: "meal_plan",    label: "Meal Plan",      Icon: CalendarDays },
  { key: "grow_at_home", label: "Grow-at-Home",   Icon: Sprout },
];

const SHARE_ACTIONS = [
  { key: "deal",     label: "Share as Deal",     Icon: Share2 },
  { key: "surplus",  label: "Share as Surplus",  Icon: Package },
  { key: "donation", label: "Share as Donation", Icon: Gift },
  { key: "store",    label: "Add to My Store",   Icon: Store },
];

function ProductActionsCard({ product, nav }) {
  const productSlug = product.slug;
  const productName = product.common_name;

  const openList = (key) => {
    // Wish/Shopping/Pantry/Donation → unified AddWishProductForm with mirror hint.
    // The 4 new saves → single POST /api/lists/saves then navigate to My Lists.
    const legacy = new Set(["wish", "shopping", "pantry", "donation"]);
    if (legacy.has(key)) {
      nav("/food-pulse/lists/wishlist/new", { state: { product, mirror: key } });
      return;
    }
    // http from @/lib/api injects the Bearer token from AuthContext ('sp_token')
    // and 401s bounce to /login. No manual localStorage read.
    http.post(`/lists/saves`, {
      list_type:       key,
      food_item_slug:  productSlug,
      food_item_name:  productName,
      food_item_image: product.hero_image || product.images?.[0]?.url,
    })
    .then(() => nav(`/food-pulse/my-lists?tab=${key}`))
    .catch((err) => {
      if (err?.response?.status === 401) {
        nav("/login?next=" + encodeURIComponent(window.location.pathname));
      } else {
        alert(err?.response?.data?.detail || "Could not save — please try again.");
      }
    });
  };

  const openShare = (key) => {
    if (key === "deal") {
      nav(`/food-pulse/spot?product=${encodeURIComponent(productSlug)}&mode=deal`);
    } else if (key === "surplus") {
      nav(`/food-pulse/spot?product=${encodeURIComponent(productSlug)}&mode=surplus`);
    } else if (key === "donation") {
      nav(`/food-pulse/lists/wishlist/new`, { state: { product, mirror: "donation", shareMode: "public" } });
    } else if (key === "store") {
      // Owner adds this product to one of their businesses. If they have
      // no business yet, funnel them into the business claim/create flow.
      nav(`/businesses?add_product=${encodeURIComponent(productSlug)}`);
    }
  };

  return (
    <div className="mt-6 grid gap-4 md:grid-cols-2" data-testid="product-actions-card">
      {/* ── Add to My Lists ─────────────────────────────────── */}
      <div className="rounded-2xl border p-4 bg-white/60" style={{ borderColor: "#B8D3A1" }}>
        <div className="font-display text-lg mb-3" style={{ color: "#1A3B1A" }}>Add to My Lists</div>
        <div className="grid grid-cols-2 gap-2">
          {LIST_ACTIONS.map(({ key, label, Icon }) => (
            <button
              key={key}
              onClick={() => openList(key)}
              className="inline-flex items-center justify-center gap-1.5 rounded-xl px-2.5 py-2.5 text-xs sm:text-sm font-semibold text-white shadow-sm hover:brightness-95 transition-all"
              style={{ background: "#7CB342" }}
              data-testid={`food-item-add-${key}`}
            >
              <Icon className="w-4 h-4" />
              <span className="truncate">{label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* ── Share or Save This Item ─────────────────────────── */}
      <div className="rounded-2xl border p-4 bg-white/60" style={{ borderColor: "#B8D3A1" }}>
        <div className="font-display text-lg mb-3" style={{ color: "#1A3B1A" }}>Share or Save This Item</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-1 gap-2">
          {SHARE_ACTIONS.map(({ key, label, Icon }) => (
            <button
              key={key}
              onClick={() => openShare(key)}
              className="inline-flex items-center justify-center gap-1.5 rounded-xl px-3 py-2.5 text-xs sm:text-sm font-semibold text-white shadow-sm hover:brightness-95 transition-all"
              style={{ background: "#7CB342" }}
              data-testid={`food-item-share-${key}`}
            >
              <Icon className="w-4 h-4" />
              <span className="truncate">{label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── PrimaryTabs — 11 chip tabs matching the FoodPulse Knowledge System
// spec. Each tab jumps to the first section within it that has data;
// tabs with no data are disabled and show a "Report missing" link
// funnelling the user into the SuggestProductForm.
//
// Deep-linking: `/food-pulse/food/tomato?tab=nutrition` will auto-scroll
// to the Nutrition tab on mount.
const PRIMARY_TABS = [
  { key: "overview",   label: "Overview",           anchors: ["basic", "profile", "names", "images"] },
  { key: "nutrition",  label: "Nutrition",          anchors: ["nutrition"] },
  { key: "health-gi",  label: "Health & GI",        anchors: ["health"] },
  { key: "prepare",    label: "Prepare & Cook",     anchors: ["consumption"] },
  { key: "storage",    label: "Storage",            anchors: ["storage"] },
  { key: "recipes",    label: "Recipes",            anchors: ["recipes", "linked-recipes"] },
  { key: "safety",     label: "Food Safety",        anchors: ["safety"] },
  { key: "recalls",    label: "Recalls",            anchors: ["recalls"] },
  { key: "news",       label: "Latest News",        anchors: ["news"] },
  { key: "research",   label: "Research",           anchors: ["refs", "links", "videos", "articles", "faqs"] },
  { key: "origins",    label: "Where It Comes From",anchors: ["cultivation", "production", "harvest", "byproducts", "environment", "economic", "culture"] },
];

function PrimaryTabs({ food, sections }) {
  const visible = new Set(sections.map((s) => s.id));
  const scrollTo = (anchors) => {
    const target = anchors.find((a) => visible.has(a));
    if (!target) return;
    const el = document.getElementById(target);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  return (
    <nav className="max-w-6xl mx-auto px-4 mt-4 sticky top-0 z-10 py-2" style={{ background: "linear-gradient(180deg,#F7FBF4 0%,#F7FBF4 90%,rgba(247,251,244,0) 100%)" }} data-testid="food-item-primary-tabs">
      <div className="flex flex-nowrap gap-2 overflow-x-auto pb-1 -mx-1 px-1">
        {PRIMARY_TABS.map(({ key, label, anchors }) => {
          const hasData = anchors.some((a) => visible.has(a));
          return (
            <div key={key} className="flex items-center gap-1 shrink-0">
              <button type="button" onClick={() => hasData && scrollTo(anchors)} disabled={!hasData}
                className="text-xs sm:text-sm font-semibold px-3 py-1.5 rounded-full border transition whitespace-nowrap"
                style={{
                  background: hasData ? "#7CB342" : "#F1EDE4",
                  color: hasData ? "#fff" : "#8A8478",
                  borderColor: hasData ? "#7CB342" : "#DDD5C8",
                  cursor: hasData ? "pointer" : "not-allowed",
                }}
                data-testid={`primary-tab-${key}`}>
                {label}
              </button>
              {!hasData && (
                <Link to={`/food-pulse/suggest-product?name=${encodeURIComponent(food.common_name)}&missing=${key}`}
                  className="text-[10px] underline opacity-70 hover:opacity-100 whitespace-nowrap"
                  data-testid={`primary-tab-${key}-report`}
                  title={`Report missing ${label.toLowerCase()} info`}>
                  Report
                </Link>
              )}
            </div>
          );
        })}
      </div>
    </nav>
  );
}



// Category-like slugs that Google may have indexed as `/food-pulse/food/<slug>`
// even though our API only stores individual food items. When a visitor
// hits one of these we redirect to the directory pre-filtered by category
// so they land on real content instead of a "not found" panel.
const CATEGORY_REDIRECT_SLUGS = new Set([
  "fruits", "vegetables", "grains", "legumes", "nuts", "seeds",
  "herbs", "spices", "dairy", "eggs", "meat", "poultry", "fish",
  "seafood", "shellfish", "beverages", "prepared", "packaged",
  "fermented", "leafy-greens", "leafy_greens", "nuts-and-seeds",
]);

export default function FoodItemPage() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const nav = useNavigate();
  const [f, setF] = useState(null);
  const [error, setError] = useState("");
  const [showAllRecipes, setShowAllRecipes] = useState(false);
  const [linkedRecipes, setLinkedRecipes] = useState({ primary: [], related: [] });
  const [linkedArticles, setLinkedArticles] = useState([]);
  const [foodNews, setFoodNews] = useState([]);
  const [recalls, setRecalls] = useState({ items: [], active: [], has_active_recall: false });

  // Fix indexed category-landing errors flagged by the SEO audit —
  // e.g. `/food-pulse/food/fruits` used to render an error panel; now
  // it silently forwards to the directory filtered by that category so
  // the visitor lands on real content and search-crawlers see a valid
  // canonical instead of a soft-404.
  useEffect(() => {
    if (slug && CATEGORY_REDIRECT_SLUGS.has(slug.toLowerCase())) {
      nav(`/food-pulse/food?category=${encodeURIComponent(slug.toLowerCase())}`, { replace: true });
    }
  }, [slug, nav]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/api/food-items/${slug}`);
        if (cancelled) return;
        setF(res.data);
        document.title = `${res.data.common_name} — Food Pulse`;
      } catch (e) {
        if (!cancelled) setError(e.response?.status === 404 ? "not_found" : "network");
      }
    })();
    // Cross-links — fire independently so slow requests don't block the main render.
    axios.get(`${API}/api/food-items/${slug}/recipes`, { params: { limit: 12 } })
      .then((r) => { if (!cancelled) setLinkedRecipes(r.data || { primary: [], related: [] }); })
      .catch(() => {});
    axios.get(`${API}/api/food-items/${slug}/articles`, { params: { limit: 6 } })
      .then((r) => { if (!cancelled) setLinkedArticles(r.data?.items || []); })
      .catch(() => {});
    // Latest food news & recalls — populate the two dynamic tabs.
    axios.get(`${API}/api/food-items/${slug}/news`, { params: { limit: 12 } })
      .then((r) => { if (!cancelled) setFoodNews(r.data?.items || []); })
      .catch(() => {});
    axios.get(`${API}/api/food-items/${slug}/recalls`, { params: { limit: 10 } })
      .then((r) => { if (!cancelled) setRecalls(r.data || { items: [], active: [], has_active_recall: false }); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [slug]);

  // Deep-link support — if `?tab=<key>` is present, scroll to the
  // first section within that tab after the page has laid out.
  useEffect(() => {
    const t = searchParams.get("tab");
    if (!t || !f) return;
    const tab = PRIMARY_TABS.find((x) => x.key === t);
    if (!tab) return;
    const raf = requestAnimationFrame(() => {
      for (const anchor of tab.anchors) {
        const el = document.getElementById(anchor);
        if (el) { el.scrollIntoView({ behavior: "smooth", block: "start" }); break; }
      }
    });
    return () => cancelAnimationFrame(raf);
  }, [searchParams, f]);

  const sections = useMemo(() => {
    if (!f) return [];
    const has = (v) => v !== undefined && v !== null && !(Array.isArray(v) && v.length === 0) && !(typeof v === "object" && !Array.isArray(v) && Object.keys(v).length === 0);
    return [
      { id: "basic",      title: "Basic Information", show: true },
      { id: "profile",    title: "Detailed Profile",  show: !!f.detailed_description },
      { id: "names",      title: "Alternate Names",   show: has(f.alternate_names) },
      { id: "images",     title: "Gallery",           show: (f.images || []).length > 1 },
      { id: "health",     title: "Health Benefits",   show: has(f.health_benefits) },
      { id: "nutrition",  title: "Nutrition",         show: has(f.nutrition) },
      { id: "consumption",title: "Consumption",       show: has(f.consumption) },
      { id: "cultivation",title: "Cultivation",       show: has(f.cultivation) },
      { id: "production", title: "Global Production", show: has(f.global_production) },
      { id: "harvest",    title: "Harvesting",        show: has(f.harvesting) },
      { id: "byproducts", title: "By-products",       show: has(f.byproducts) },
      { id: "storage",    title: "Storage",           show: has(f.storage) },
      { id: "recipes",    title: "Recipes",           show: has(f.recipes) },
      { id: "linked-recipes", title: "Cook it — recipes",
        show: (linkedRecipes.primary.length + linkedRecipes.related.length) > 0 },
      { id: "articles",   title: "Read more",
        show: linkedArticles.length > 0 },
      { id: "safety",     title: "Food Safety",       show: has(f.food_safety) },
      // Dynamic: recalls + news — these come from separate endpoints
      // and are visible only when data arrived. Always list them in
      // the sections array so the primary tab bar can gate on them.
      { id: "recalls",    title: "Recalls & Safety Alerts",
        show: recalls.items.length > 0 },
      { id: "news",       title: "Latest News",       show: foodNews.length > 0 },
      { id: "environment",title: "Environmental Impact", show: has(f.environmental_impact) },
      { id: "economic",   title: "Economic Info",     show: has(f.economic_info) },
      { id: "culture",    title: "Cultural Importance", show: has(f.cultural_importance) },
      { id: "facts",      title: "Interesting Facts", show: (f.interesting_facts || []).length > 0 },
      { id: "faqs",       title: "FAQs",              show: (f.faqs || []).length > 0 },
      { id: "refs",       title: "References",        show: (f.references || []).length > 0 },
      { id: "links",      title: "Website Links",     show: (f.website_links || []).length > 0 },
      { id: "videos",     title: "Videos",            show: (f.video_links || []).length > 0 },
      { id: "meta",       title: "Related Foods",     show: has(f.ai_metadata?.related_foods) || has(f.ai_metadata?.similar_foods) },
    ].filter((s) => s.show);
  }, [f, linkedRecipes, linkedArticles, foodNews, recalls]);

  if (error === "not_found") {
    return (
      <div className="max-w-3xl mx-auto px-4 py-20 text-center" data-testid="food-item-not-found">
        <Leaf className="w-8 h-8 mx-auto mb-2 opacity-40" />
        <div className="font-display text-2xl mb-1">We haven't profiled this food yet.</div>
        <div className="text-sm opacity-70 mb-6">Help us add it to the catalog — we'll research the nutrition, cooking tips and recipes and publish it for everyone.</div>
        <div className="flex flex-wrap gap-3 justify-center">
          <Link to={`/food-pulse/suggest-product?name=${encodeURIComponent(slug.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()))}`}
                className="rounded-full px-5 py-2.5 font-semibold text-white"
                style={{ background: "#0F3D2E" }}
                data-testid="food-item-notfound-suggest">
            Suggest this product
          </Link>
          <Link to="/food-pulse/food" className="rounded-full px-5 py-2.5 border font-semibold" style={{ borderColor: "#0F3D2E", color: "#0F3D2E" }}>
            <ArrowLeft className="w-4 h-4 inline mr-1 -mt-0.5" /> Back to directory
          </Link>
        </div>
      </div>
    );
  }
  if (error === "network") {
    return (
      <div className="max-w-3xl mx-auto px-4 py-20 text-center" data-testid="food-item-error">
        <AlertTriangle className="w-8 h-8 mx-auto mb-2 opacity-60 text-red-700" />
        <div className="font-display text-2xl mb-1">Couldn't load this food.</div>
        <div className="text-sm opacity-70 mb-4">Something went wrong on our side. Please try again in a moment.</div>
        <button onClick={() => window.location.reload()} className="rounded-full px-4 py-2 text-sm font-semibold text-white" style={{ background: "#2F6B2F" }} data-testid="food-item-retry">Retry</button>
        <Link to="/food-pulse/food" className="inline-flex items-center gap-1 ml-3 text-sm underline">
          <ArrowLeft className="w-3 h-3" /> Back to directory
        </Link>
      </div>
    );
  }
  if (!f) {
    return <div className="max-w-3xl mx-auto px-4 py-20 text-sm opacity-70" data-testid="food-item-loading">Loading…</div>;
  }

  const cover = (f.images || [])[0];
  const recipes = f.recipes || [];
  const shownRecipes = showAllRecipes ? recipes : recipes.slice(0, 6);

  return (
    <div className="min-h-screen" style={{ background: "#F7FBF4" }} data-testid={`food-item-page-${slug}`}>
      <SeoHead
        section="food-pulse"
        title={f.ai_metadata?.seo_title || `${f.common_name} — Food Pulse Profile`}
        description={f.ai_metadata?.seo_description || f.short_description || f.description || undefined}
      />

      {/* Hero */}
      <section className="border-b" style={{ borderColor: "#DCEAD1", background: "linear-gradient(180deg,#DCEAD1 0%,#F7FBF4 100%)" }}>
        <div className="max-w-6xl mx-auto px-4 py-10 grid md:grid-cols-[minmax(0,1fr)_320px] gap-8 items-start">
          <div>
            <Link to="/food-pulse/food" className="inline-flex items-center gap-1 text-xs mb-3 opacity-70 hover:opacity-100" data-testid="food-item-back">
              <ArrowLeft className="w-3 h-3" /> Back to Food Pulse directory
            </Link>
            <div className="text-[10px] font-mono uppercase tracking-widest" style={{ color: "#2F6B2F" }}>
              {f.category?.replace(/-/g, " ") || "food"}
              {f.food_family ? ` · ${f.food_family}` : ""}
            </div>
            <h1 className="font-display font-bold leading-tight text-4xl sm:text-5xl mt-1" style={{ color: "#1A3B1A" }} data-testid="food-item-h1">{f.common_name}</h1>
            {f.scientific_name && <div className="text-base italic mt-1 opacity-70">{f.scientific_name}</div>}
            {(f.short_description || f.description) && <p className="mt-4 text-sm sm:text-base max-w-2xl leading-relaxed" style={{ color: "#3E5A3E" }}>{f.short_description || f.description}</p>}
            <ProductActionsCard
              product={{
                slug: f.slug, common_name: f.common_name,
                hero_image: cover?.url, category: f.category,
              }}
              nav={nav}
            />
            {f.content_open && (
              <div className="mt-3 inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-wider px-2.5 py-1 rounded-full"
                   style={{ background: "#F0F7EA", color: "#2F6B2F", border: "1px solid #B8D3A1" }}
                   data-testid="food-item-open-badge"
                   title="This profile is an editorial draft — we welcome corrections and additions.">
                <span>✎</span> Draft profile · <span className="opacity-80">open to community additions</span>
              </div>
            )}
          </div>
          {cover?.url && (
            <img src={cover.url} alt={cover.caption || f.common_name} className="rounded-2xl object-cover w-full aspect-[4/3] shadow-sm" />
          )}
        </div>
      </section>

      {/* ── Product Characteristics · Identifier strip ─────────── */}
      <IdentifierStrip characteristics={f.characteristics} apiBase={API} slug={slug} productName={f.common_name} />

      {/* ── Nearby prices & offers (Phase 1 — Smart Shopping) ─── */}
      <NearbyPricesBlock productSlug={slug} productName={f.common_name} />

      {/* ── Farmers currently posting this crop (FarmersPulse bridge) ── */}
      <div className="max-w-6xl mx-auto px-4 mt-3">
        <FarmersPostingStrip slug={slug} />
      </div>

      {/* ── Primary chip-tab bar (Article · Nutrition · Recipes · Tips & Links) ── */}
      <PrimaryTabs food={f} sections={sections} />

      {/* Table of contents (fine-grained, all sections) */}
      <nav className="max-w-6xl mx-auto px-4 py-6" data-testid="food-item-toc">
        <div className="text-[10px] font-mono uppercase tracking-widest opacity-60 mb-2">In this profile</div>
        <div className="flex flex-wrap gap-1.5">
          {sections.map((s) => (
            <a key={s.id} href={`#${s.id}`} className="text-xs px-2.5 py-1 rounded-full border hover:bg-white" style={{ borderColor: "#B8D3A1", background: "#fff", color: "#2F6B2F" }} data-testid={`food-toc-${s.id}`}>
              {s.title}
            </a>
          ))}
        </div>
      </nav>

      <div className="max-w-6xl mx-auto px-4 pb-16 space-y-4">
        {/* Basic info */}
        <Section id="basic" title="Basic Information">
          <div className="grid sm:grid-cols-2 gap-3">
            <KV label="Common name" value={f.common_name} />
            <KV label="Scientific name" value={f.scientific_name} />
            <KV label="Category" value={f.category} />
            <KV label="Food family" value={f.food_family} />
            <KV label="Botanical classification" value={f.botanical_classification} />
            <KV label="Variety" value={f.variety} />
            <KV label="USDA classification" value={f.usda_class} />
          </div>
        </Section>

        {/* Detailed profile — long-form encyclopedic article (markdown
            rendered server-side into HTML). Only surfaces when the
            food_items doc has been enriched with a
            ``detailed_description`` payload from load_food_profile.py
            (or, later, the LLM enrichment worker). Uses the same
            typographic scale as the rest of the page and inherits the
            parchment palette. */}
        {f.detailed_description && (
          <Section id="profile" title="Detailed Profile">
            {f.profile_last_updated && (
              <div className="text-[10px] font-mono uppercase tracking-wider mb-3 opacity-60">
                Last reviewed {new Date(f.profile_last_updated).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}
              </div>
            )}
            <article
              className="prose-article fp-longform"
              data-testid="food-item-detailed-description"
              dangerouslySetInnerHTML={{ __html: f.detailed_description }}
            />
          </Section>
        )}

        {/* Alternate names */}
        {f.alternate_names && Object.keys(f.alternate_names).length > 0 && (
          <Section id="names" title="Alternate Names">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {Object.entries(f.alternate_names).map(([lang, name]) => (
                <div key={lang} className="text-sm"><span className="opacity-60 uppercase text-[10px] font-mono mr-2">{lang}</span>{name}</div>
              ))}
            </div>
          </Section>
        )}

        {/* Gallery */}
        {(f.images || []).length > 1 && (
          <Section id="images" title="Gallery">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {(f.images || []).slice(0, 4).map((img, i) => (
                <figure key={i} className="rounded-xl overflow-hidden bg-black/5">
                  <img src={img.url} alt={img.caption || f.common_name} className="w-full aspect-square object-cover" loading="lazy" />
                  <figcaption className="text-[10px] font-mono opacity-70 p-1">{img.kind || ""} {img.caption ? `· ${img.caption}` : ""}</figcaption>
                </figure>
              ))}
            </div>
          </Section>
        )}

        {/* Health */}
        {f.health_benefits && (
          <Section id="health" title="Health Benefits">
            {f.health_benefits.major?.length > 0 && (
              <div className="mb-3">
                <div className="text-[10px] font-mono uppercase tracking-widest opacity-60 mb-1">Major benefits</div>
                <ul className="list-disc list-inside space-y-0.5">{f.health_benefits.major.map((m, i) => <li key={i}>{m}</li>)}</ul>
              </div>
            )}
            <div className="grid sm:grid-cols-2 gap-3">
              <KV label="Evidence" value={f.health_benefits.evidence} />
              <KV label="Immune" value={f.health_benefits.immune} />
              <KV label="Heart" value={f.health_benefits.heart} />
              <KV label="Brain" value={f.health_benefits.brain} />
              <KV label="Digestive" value={f.health_benefits.digestive} />
              <KV label="Bone" value={f.health_benefits.bone} />
              <KV label="Skin" value={f.health_benefits.skin} />
              <KV label="Eye" value={f.health_benefits.eye} />
            </div>
            {f.health_benefits.disease_prevention?.length > 0 && (
              <div className="mt-3">
                <div className="text-[10px] font-mono uppercase tracking-widest opacity-60 mb-1">Disease prevention</div>
                <div className="flex flex-wrap">{f.health_benefits.disease_prevention.map(pill)}</div>
              </div>
            )}
          </Section>
        )}

        {/* Nutrition */}
        {f.nutrition && (
          <Section id="nutrition" title="Nutrition (per 100 g)">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {Object.entries(f.nutrition.per_100g || {}).filter(([, v]) => typeof v !== "object" || v === null).map(([k, v]) => (
                <KV key={k} label={k.replace(/_/g, " ")} value={v} />
              ))}
              <KV label="Glycemic index" value={f.nutrition.glycemic_index} />
              <KV label="Glycemic load" value={f.nutrition.glycemic_load} />
              <KV label="Serving (g)" value={f.nutrition.recommended_serving_g} />
              <KV label="Daily intake" value={f.nutrition.recommended_daily_intake} />
            </div>
            {f.nutrition.suitable_diets && (
              <div className="mt-3">
                <div className="text-[10px] font-mono uppercase tracking-widest opacity-60 mb-1">Suitable diets</div>
                <div className="flex flex-wrap">
                  {Object.entries(f.nutrition.suitable_diets)
                    .filter(([, v]) => v === true)
                    .map(([k], i) => pill(k.replace(/_/g, " "), i))}
                </div>
              </div>
            )}
          </Section>
        )}

        {/* Consumption */}
        {f.consumption && (
          <Section id="consumption" title="Consumption">
            <div className="grid sm:grid-cols-3 gap-3">
              <KV label="Taste" value={f.consumption.taste} />
              <KV label="Texture" value={f.consumption.texture} />
              <KV label="Aroma" value={f.consumption.aroma} />
            </div>
            {f.consumption.methods?.length > 0 && (
              <div className="mt-3"><div className="text-[10px] font-mono uppercase tracking-widest opacity-60 mb-1">Methods</div><div className="flex flex-wrap">{f.consumption.methods.map(pill)}</div></div>
            )}
            {f.consumption.culinary_uses?.length > 0 && (
              <div className="mt-3"><div className="text-[10px] font-mono uppercase tracking-widest opacity-60 mb-1">Culinary uses</div><ul className="list-disc list-inside">{f.consumption.culinary_uses.map((c, i) => <li key={i}>{c}</li>)}</ul></div>
            )}
          </Section>
        )}

        {/* Cultivation */}
        {f.cultivation && (
          <Section id="cultivation" title="Cultivation">
            <div className="grid sm:grid-cols-2 gap-3">
              <KV label="Countries" value={(f.cultivation.countries || []).join(", ")} />
              <KV label="Regions" value={(f.cultivation.regions || []).join(", ")} />
              <KV label="Climate" value={f.cultivation.climate} />
              <KV label="Temperature (°C)" value={f.cultivation.temperature_c} />
              <KV label="Rainfall (mm)" value={f.cultivation.rainfall_mm} />
              <KV label="Soil" value={f.cultivation.soil} />
              <KV label="Growing season" value={f.cultivation.growing_season} />
              <KV label="Irrigation" value={f.cultivation.irrigation} />
              <KV label="Fertilizer" value={f.cultivation.fertilizer} />
              <KV label="Organic" value={f.cultivation.organic} />
              <KV label="Greenhouse" value={f.cultivation.greenhouse} />
              <KV label="Home gardening" value={f.cultivation.home_gardening} />
            </div>
            {(f.cultivation.common_pests || []).length > 0 && <div className="mt-3"><div className="text-[10px] font-mono uppercase tracking-widest opacity-60 mb-1">Common pests</div><div className="flex flex-wrap">{f.cultivation.common_pests.map(pill)}</div></div>}
            {(f.cultivation.common_diseases || []).length > 0 && <div className="mt-2"><div className="text-[10px] font-mono uppercase tracking-widest opacity-60 mb-1">Common diseases</div><div className="flex flex-wrap">{f.cultivation.common_diseases.map(pill)}</div></div>}
            {(f.cultivation.sustainable_practices || []).length > 0 && <div className="mt-2"><div className="text-[10px] font-mono uppercase tracking-widest opacity-60 mb-1">Sustainable practices</div><ul className="list-disc list-inside">{f.cultivation.sustainable_practices.map((s, i) => <li key={i}>{s}</li>)}</ul></div>}
          </Section>
        )}

        {/* Production */}
        {f.global_production && (
          <Section id="production" title="Global Production">
            {(f.global_production.top_producers || []).length > 0 && (
              <div className="mb-3">
                <div className="text-[10px] font-mono uppercase tracking-widest opacity-60 mb-1">Top producers</div>
                <div className="grid sm:grid-cols-2 gap-2">
                  {f.global_production.top_producers.map((tp, i) => (
                    <div key={i} className="rounded-lg border p-2" style={{ borderColor: "#DCEAD1" }}>
                      <div className="font-semibold text-sm">{tp.country}</div>
                      <div className="text-xs opacity-70">
                        {tp.share_pct != null ? `${tp.share_pct}%` : ""}
                        {tp.annual_tonnes != null ? ` · ${tp.annual_tonnes.toLocaleString()} t/yr` : ""}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="grid sm:grid-cols-2 gap-3">
              <KV label="Exporters" value={(f.global_production.exporters || []).join(", ")} />
              <KV label="Importers" value={(f.global_production.importers || []).join(", ")} />
              <KV label="Seasonal availability" value={f.global_production.seasonal_availability} />
              <KV label="Economic importance" value={f.global_production.economic_importance} />
              <KV label="Market trends" value={f.global_production.market_trends} />
            </div>
          </Section>
        )}

        {/* Harvest */}
        {f.harvesting && (
          <Section id="harvest" title="Harvesting">
            <div className="grid sm:grid-cols-2 gap-3">
              <KV label="Season" value={f.harvesting.season} />
              <KV label="Methods" value={(f.harvesting.methods || []).join(", ")} />
              <KV label="Grading" value={f.harvesting.grading} />
              <KV label="Packaging" value={f.harvesting.packaging} />
              <KV label="Transportation" value={f.harvesting.transportation} />
              <KV label="Shelf life after harvest" value={f.harvesting.shelf_life_after_harvest} />
            </div>
          </Section>
        )}

        {/* Byproducts */}
        {(f.byproducts || []).length > 0 && (
          <Section id="byproducts" title="By-products">
            <ul className="space-y-2">
              {f.byproducts.map((b, i) => (
                <li key={i}><span className="font-semibold">{b.name}</span> — <span className="opacity-80">{b.description}</span></li>
              ))}
            </ul>
          </Section>
        )}

        {/* Storage */}
        {f.storage && (
          <Section id="storage" title="Storage">
            <div className="grid sm:grid-cols-2 gap-3">
              <KV label="Temperature (°C)" value={f.storage.temperature_c} />
              <KV label="Humidity (%)" value={f.storage.humidity_pct} />
              <KV label="Refrigeration" value={f.storage.refrigeration} />
              <KV label="Freezing" value={f.storage.freezing} />
              <KV label="Shelf life" value={f.storage.shelf_life} />
              <KV label="Controlled atmosphere" value={f.storage.controlled_atmosphere} />
              <KV label="Safety notes" value={f.storage.safety_notes} />
            </div>
            {(f.storage.preservation || []).length > 0 && <div className="mt-3"><div className="text-[10px] font-mono uppercase tracking-widest opacity-60 mb-1">Preservation</div><div className="flex flex-wrap">{f.storage.preservation.map(pill)}</div></div>}
          </Section>
        )}

        {/* Recipes */}
        {(f.recipes || []).length > 0 && (
          <Section id="recipes" title={`Recipes (${f.recipes.length})`}>
            <div className="grid gap-3 sm:grid-cols-2">
              {shownRecipes.map((r, i) => (
                <article key={i} className="rounded-xl border p-3" style={{ borderColor: "#DCEAD1" }} data-testid={`food-recipe-${i}`}>
                  <div className="text-[10px] font-mono uppercase tracking-widest opacity-60">{r.country} · {r.difficulty}</div>
                  <div className="font-display text-base mt-0.5"><Utensils className="w-3 h-3 inline mr-1 opacity-60" />{r.name}</div>
                  <p className="text-xs mt-1 opacity-80">{r.description}</p>
                  <div className="text-[10px] font-mono opacity-60 mt-2">
                    Prep {r.prep_time_min}m · Cook {r.cook_time_min}m · Serves {r.servings}
                  </div>
                </article>
              ))}
            </div>
            {recipes.length > 6 && (
              <button onClick={() => setShowAllRecipes(!showAllRecipes)} className="mt-4 text-xs underline" data-testid="food-recipes-toggle">
                {showAllRecipes ? "Show fewer recipes" : `Show all ${recipes.length} recipes`}
              </button>
            )}
          </Section>
        )}

        {/* Linked recipes from the standalone recipes collection */}
        {(linkedRecipes.primary.length + linkedRecipes.related.length) > 0 && (
          <Section id="linked-recipes"
                   title={`Cook it · ${linkedRecipes.primary.length + linkedRecipes.related.length} recipe${(linkedRecipes.primary.length + linkedRecipes.related.length) === 1 ? "" : "s"}`}>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {[...linkedRecipes.primary, ...linkedRecipes.related].slice(0, 12).map((r) => (
                <Link key={r.slug || r.id}
                      to={`/recipes/${r.slug || r.id}`}
                      className="rounded-xl border overflow-hidden bg-white hover:-translate-y-0.5 hover:shadow-md transition-all block"
                      style={{ borderColor: "#DCEAD1" }}
                      data-testid={`linked-recipe-${r.slug || r.id}`}>
                  {r.cover_photo && (
                    <div className="aspect-[4/3] overflow-hidden">
                      <img src={r.cover_photo} alt={r.title}
                           className="w-full h-full object-cover" loading="lazy" />
                    </div>
                  )}
                  <div className="p-3">
                    {(r.cuisine || r.country || r.difficulty) && (
                      <div className="text-[10px] font-mono uppercase tracking-widest opacity-60">
                        {[r.cuisine, r.country, r.difficulty].filter(Boolean).join(" · ")}
                      </div>
                    )}
                    <div className="font-display text-base mt-0.5 line-clamp-2"><Utensils className="w-3 h-3 inline mr-1 opacity-60" />{r.title}</div>
                    {r.short_description && (
                      <p className="text-xs mt-1 opacity-70 line-clamp-2">{r.short_description}</p>
                    )}
                    {(r.total_time_min || r.servings) && (
                      <div className="text-[10px] font-mono opacity-60 mt-2">
                        {r.total_time_min && `${r.total_time_min}m`}
                        {r.total_time_min && r.servings && " · "}
                        {r.servings && `Serves ${r.servings}`}
                      </div>
                    )}
                  </div>
                </Link>
              ))}
            </div>
            {(linkedRecipes.primary.length + linkedRecipes.related.length) > 12 && (
              <Link to={`/food-pulse/recipes?ingredient=${f.slug}`}
                    className="mt-4 inline-block text-xs underline"
                    data-testid="linked-recipes-see-all">
                See all {linkedRecipes.primary.length + linkedRecipes.related.length} recipes →
              </Link>
            )}
          </Section>
        )}

        {/* Linked long-form articles from the Ghost catalogue */}
        {linkedArticles.length > 0 && (
          <Section id="articles" title={`Read more · ${linkedArticles.length} article${linkedArticles.length === 1 ? "" : "s"}`}>
            <div className="grid gap-3 sm:grid-cols-2">
              {linkedArticles.map((a) => (
                <Link key={a.slug} to={`/food-pulse/articles/${a.slug}`}
                      className="flex gap-3 rounded-xl border p-3 bg-white hover:-translate-y-0.5 hover:shadow-md transition-all"
                      style={{ borderColor: "#DCEAD1" }}
                      data-testid={`linked-article-${a.slug}`}>
                  {a.cover_image && (
                    <img src={a.cover_image} alt={a.title}
                         className="w-24 h-24 rounded-lg object-cover flex-none border"
                         style={{ borderColor: "#DCEAD1" }} loading="lazy" />
                  )}
                  <div className="min-w-0">
                    <div className="text-[10px] font-mono uppercase tracking-widest opacity-60">{a.category || a.tag}</div>
                    <div className="font-display text-base line-clamp-2">{a.title}</div>
                    {a.excerpt && <p className="text-xs opacity-70 mt-1 line-clamp-2">{a.excerpt}</p>}
                  </div>
                </Link>
              ))}
            </div>
          </Section>
        )}

        {/* Safety */}
        {f.food_safety && (
          <Section id="safety" title="Food Safety">
            {(f.food_safety.allergies || []).length > 0 && <div className="mb-2"><div className="text-[10px] font-mono uppercase tracking-widest opacity-60 mb-1">Allergies</div><div className="flex flex-wrap">{f.food_safety.allergies.map(pill)}</div></div>}
            {(f.food_safety.toxic_parts || []).length > 0 && <div className="mb-2"><div className="text-[10px] font-mono uppercase tracking-widest opacity-60 mb-1">Toxic parts</div><div className="flex flex-wrap">{f.food_safety.toxic_parts.map(pill)}</div></div>}
            {(f.food_safety.precautions || []).length > 0 && <div className="mb-2"><div className="text-[10px] font-mono uppercase tracking-widest opacity-60 mb-1">Precautions</div><ul className="list-disc list-inside">{f.food_safety.precautions.map((p, i) => <li key={i}>{p}</li>)}</ul></div>}
            <KV label="Safe handling" value={f.food_safety.safe_handling} />
            <KV label="Storage safety" value={f.food_safety.storage_safety} />
            {(f.food_safety.foodborne_illness_risks || []).length > 0 && <div className="mt-2"><div className="text-[10px] font-mono uppercase tracking-widest opacity-60 mb-1">Foodborne illness risks</div><ul className="list-disc list-inside">{f.food_safety.foodborne_illness_risks.map((r, i) => <li key={i}>{r}</li>)}</ul></div>}
          </Section>
        )}

        {/* Recalls — dynamic per-food. Always render the section header
            when we have any recalls (active OR historic). Active recalls
            get a red banner up top per spec §34. */}
        {recalls.items.length > 0 && (
          <Section id="recalls" title="Recalls & Safety Alerts">
            {recalls.has_active_recall && (
              <div className="mb-4 rounded-xl border p-3 flex items-start gap-2" style={{ borderColor: "#F5B4B4", background: "#FDECEC", color: "#8A1F1F" }} data-testid="food-recalls-active-banner">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest">🔴 Active recall</div>
                  <div className="text-sm">{recalls.active.length} active recall{recalls.active.length > 1 ? "s" : ""} match this food. Check package, brand and lot before use.</div>
                </div>
              </div>
            )}
            <ul className="space-y-2">
              {recalls.items.slice(0, 8).map((rc, i) => (
                <li key={i} className="rounded-lg border p-3" style={{ borderColor: "#DCEAD1" }} data-testid={`food-recall-${i}`}>
                  <div className="flex items-baseline justify-between gap-2">
                    <div className="text-[10px] font-mono uppercase tracking-widest" style={{ color: rc.status === "active" ? "#8A1F1F" : "#6B4A2E" }}>
                      {rc.status === "active" ? "🔴 Active" : rc.status === "resolved" ? "🟢 Resolved" : "🟡 Info"}
                      {rc.agency ? ` · ${rc.agency}` : ""}
                    </div>
                    {rc.recall_date && <div className="text-[10px] opacity-60">{new Date(rc.recall_date).toLocaleDateString()}</div>}
                  </div>
                  <div className="font-semibold text-sm mt-0.5">{rc.title || rc.product_name || "Recall notice"}</div>
                  {rc.brand && <div className="text-xs opacity-70">Brand: {rc.brand}</div>}
                  {rc.reason && <p className="text-xs mt-1 opacity-80 line-clamp-3">{rc.reason}</p>}
                  {rc.source_url && (
                    <a href={rc.source_url} target="_blank" rel="noopener noreferrer" className="text-xs underline mt-1 inline-block" style={{ color: "#2F6B2F" }}>
                      Source <ExternalLink className="inline w-3 h-3 -mt-0.5" />
                    </a>
                  )}
                </li>
              ))}
            </ul>
            <div className="mt-3 text-[11px] opacity-60">
              A recall for one specific brand/SKU does not mean this generic food is unsafe.
              Confirm the UPC, brand and lot on the recall notice before discarding.
            </div>
          </Section>
        )}

        {/* Latest News — dynamic per-food, populated from the FoodPulse
            Google-News RSS aggregator. Metadata + link only (no full
            article body) to respect publisher copyright per spec §37. */}
        {foodNews.length > 0 && (
          <Section id="news" title="Latest News">
            <ul className="space-y-2">
              {foodNews.slice(0, 12).map((n, i) => (
                <li key={i} className="rounded-lg border p-3" style={{ borderColor: "#DCEAD1" }} data-testid={`food-news-${i}`}>
                  <div className="flex items-baseline justify-between gap-2">
                    <div className="text-[10px] font-mono uppercase tracking-widest opacity-70">
                      <Newspaper className="w-3 h-3 inline mr-1" />
                      {n.publisher || n.source || "News"}
                      {n.category ? ` · ${n.category}` : ""}
                    </div>
                    {n.publication_date && (
                      <div className="text-[10px] opacity-60">{new Date(n.publication_date).toLocaleDateString()}</div>
                    )}
                  </div>
                  <a href={n.source_url || n.canonical_url || "#"} target="_blank" rel="noopener noreferrer"
                     className="font-semibold text-sm mt-0.5 block hover:underline" style={{ color: "#1A3B1A" }}>
                    {n.title}
                  </a>
                  {n.summary && <p className="text-xs mt-1 opacity-80 line-clamp-2">{n.summary}</p>}
                </li>
              ))}
            </ul>
          </Section>
        )}

        {/* Environment */}
        {f.environmental_impact && (
          <Section id="environment" title="Environmental Impact">
            <div className="grid sm:grid-cols-2 gap-3">
              <KV label="Water footprint (L/kg)" value={f.environmental_impact.water_footprint_l_per_kg} />
              <KV label="Carbon footprint (kg CO₂e/kg)" value={f.environmental_impact.carbon_footprint_kg_co2e_per_kg} />
              <KV label="Sustainability rating" value={f.environmental_impact.sustainability_rating} />
              <KV label="Organic production" value={f.environmental_impact.organic_production} />
              <KV label="Regenerative agriculture" value={f.environmental_impact.regenerative_agriculture} />
              <KV label="Biodiversity impact" value={f.environmental_impact.biodiversity_impact} />
            </div>
          </Section>
        )}

        {/* Economic */}
        {f.economic_info && (
          <Section id="economic" title="Economic Info">
            <div className="grid sm:grid-cols-2 gap-3">
              <KV label="Retail (USD/kg)" value={f.economic_info.retail_price_range_usd_per_kg} />
              <KV label="Wholesale (USD/kg)" value={f.economic_info.wholesale_price_range_usd_per_kg} />
              <KV label="Seasonal price trends" value={f.economic_info.seasonal_price_trends} />
              <KV label="Global trade value" value={f.economic_info.global_trade_value_usd} />
            </div>
          </Section>
        )}

        {/* Culture */}
        {f.cultural_importance && (
          <Section id="culture" title="Cultural Importance">
            <div className="grid sm:grid-cols-2 gap-3">
              <KV label="Historical" value={f.cultural_importance.historical} />
              <KV label="Religious" value={f.cultural_importance.religious} />
            </div>
            {(f.cultural_importance.festivals || []).length > 0 && <div className="mt-3"><div className="text-[10px] font-mono uppercase tracking-widest opacity-60 mb-1">Festivals</div><div className="flex flex-wrap">{f.cultural_importance.festivals.map(pill)}</div></div>}
            {(f.cultural_importance.ceremonies || []).length > 0 && <div className="mt-2"><div className="text-[10px] font-mono uppercase tracking-widest opacity-60 mb-1">Ceremonies</div><div className="flex flex-wrap">{f.cultural_importance.ceremonies.map(pill)}</div></div>}
            {(f.cultural_importance.indigenous_uses || []).length > 0 && <div className="mt-2"><div className="text-[10px] font-mono uppercase tracking-widest opacity-60 mb-1">Indigenous uses</div><ul className="list-disc list-inside">{f.cultural_importance.indigenous_uses.map((u, i) => <li key={i}>{u}</li>)}</ul></div>}
            {(f.cultural_importance.folklore || []).length > 0 && <div className="mt-2"><div className="text-[10px] font-mono uppercase tracking-widest opacity-60 mb-1">Folklore</div><ul className="list-disc list-inside">{f.cultural_importance.folklore.map((s, i) => <li key={i}>{s}</li>)}</ul></div>}
          </Section>
        )}

        {/* Facts */}
        {(f.interesting_facts || []).length > 0 && (
          <Section id="facts" title="Interesting Facts">
            <ul className="list-disc list-inside space-y-1">{f.interesting_facts.map((s, i) => <li key={i}>{s}</li>)}</ul>
          </Section>
        )}

        {/* FAQs */}
        {(f.faqs || []).length > 0 && (
          <Section id="faqs" title="Frequently Asked Questions">
            <div className="space-y-3">
              {f.faqs.map((qa, i) => (
                <details key={i} className="rounded-xl border p-3" style={{ borderColor: "#DCEAD1" }}>
                  <summary className="cursor-pointer font-semibold">{qa.q}</summary>
                  <p className="mt-2 text-sm opacity-80">{qa.a}</p>
                </details>
              ))}
            </div>
          </Section>
        )}

        {/* References */}
        {(f.references || []).length > 0 && (
          <Section id="refs" title="References">
            <ul className="space-y-1">{f.references.map((r, i) => (
              <li key={i}>
                <a href={r.url} target="_blank" rel="noopener noreferrer" className="text-sm underline inline-flex items-center gap-1">
                  {r.title} <ExternalLink className="w-3 h-3" />
                </a>
                {r.publisher && <span className="text-xs opacity-60 ml-2">· {r.publisher}</span>}
              </li>
            ))}</ul>
          </Section>
        )}

        {/* Links */}
        {(f.website_links || []).length > 0 && (
          <Section id="links" title="Website Links">
            <ul className="space-y-1">{f.website_links.map((r, i) => (
              <li key={i}><a href={r.url} target="_blank" rel="noopener noreferrer" className="text-sm underline inline-flex items-center gap-1">{r.title} <ExternalLink className="w-3 h-3" /></a></li>
            ))}</ul>
          </Section>
        )}

        {/* Videos */}
        {(f.video_links || []).length > 0 && (
          <Section id="videos" title="Videos">
            <ul className="space-y-1">{f.video_links.map((r, i) => (
              <li key={i} className="text-sm"><span className="opacity-60 text-[10px] font-mono uppercase tracking-widest mr-2">{r.kind}</span><a href={r.url} target="_blank" rel="noopener noreferrer" className="underline inline-flex items-center gap-1">{r.title} <ExternalLink className="w-3 h-3" /></a></li>
            ))}</ul>
          </Section>
        )}

        {/* AI metadata (related foods) */}
        {(f.ai_metadata?.related_foods?.length > 0 || f.ai_metadata?.similar_foods?.length > 0) && (
          <Section id="meta" title="Related Foods">
            {(f.ai_metadata?.related_foods || []).length > 0 && (
              <div className="mb-3">
                <div className="text-[10px] font-mono uppercase tracking-widest opacity-60 mb-1">Related</div>
                <div className="flex flex-wrap gap-1.5">
                  {f.ai_metadata.related_foods.map((slug2, i) => (
                    <Link key={i} to={`/food-pulse/food/${slug2}`} className="text-xs px-2.5 py-1 rounded-full border" style={{ borderColor: "#B8D3A1", background: "#fff", color: "#2F6B2F" }}>{slug2}</Link>
                  ))}
                </div>
              </div>
            )}
            {(f.ai_metadata?.similar_foods || []).length > 0 && (
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest opacity-60 mb-1">Similar</div>
                <div className="flex flex-wrap gap-1.5">
                  {f.ai_metadata.similar_foods.map((slug2, i) => (
                    <Link key={i} to={`/food-pulse/food/${slug2}`} className="text-xs px-2.5 py-1 rounded-full border" style={{ borderColor: "#B8D3A1", background: "#fff", color: "#2F6B2F" }}>{slug2}</Link>
                  ))}
                </div>
              </div>
            )}
          </Section>
        )}

        {/* Source footer */}
        {f.source && (
          <div className="text-[10px] font-mono opacity-50 mt-4">
            <Sparkles className="inline w-3 h-3 mr-1" />
            Profile source: {f.source}
            {f.updated_at ? ` · updated ${new Date(f.updated_at).toLocaleDateString()}` : ""}
          </div>
        )}
      </div>
    </div>
  );
}


// ─── Product Characteristics · Identifier strip ─────────────────
// Renders under the hero:
//   • Identifier card — barcode strip (or "no barcode" placeholder),
//     QR badge, FoodPulse Product ID, short URL (with copy button)
//   • Dietary chips (vegan / veg / GF / halal / …)
//   • GHS Hazard card (only for products with `food_safety.ghs_hazard`
//     — typically non-food cleaning / automotive / pest-control)
//   • Certification ribbons
//
// URL normalization is centralized in `@/lib/splinkUrl` — the DB may
// hold legacy `fp.to` links + mixed absolute/relative asset paths, so
// every display of a short URL / QR / barcode goes through the helpers.
function IdentifierStrip({ characteristics, apiBase, slug, productName }) {
  const [copied, setCopied] = useState(false);
  const chars = characteristics || {};
  const ids   = chars.identifiers || {};
  const diet  = chars.dietary     || {};
  const safety = chars.food_safety || {};
  const ghs    = safety.ghs_hazard   || null;
  const certs  = (ids.other_certifications || []).slice();
  const nutrition = chars.nutrition || {};

  // Normalise every URL we're about to render. Legacy fp.to → splink.pro,
  // relative asset paths → prefixed with apiBase, missing QRs synthesized
  // from the short URL.
  const shortUrl     = normalizeShortUrl(ids.short_url);
  const qrCodeUrlRaw = ids.qr_code_url || synthesizeQrFromShortUrl(shortUrl);
  const qrCodeUrl    = normalizeAssetUrl(qrCodeUrlRaw, apiBase);
  const barcodeUrl   = normalizeAssetUrl(ids.barcode_image_url, apiBase);

  const hasIdentifierBlock = ids.foodpulse_product_id || shortUrl;
  const hasDietary = Object.values(diet).some((v) => v === true);
  const hasCerts   = certs.length > 0 || ids.usda_organic || ids.kosher_id || ids.halal_id || ids.non_gmo_id || ids.fair_trade_id || ids.rainforest_alliance_id;

  // Nothing to render → save a section
  if (!hasIdentifierBlock && !hasDietary && !ghs && !hasCerts) return null;

  const copyShortUrl = async () => {
    if (!shortUrl) return;
    try {
      await navigator.clipboard.writeText(shortUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard may not be available */ }
  };

  const dietChips = [
    ["vegan",       "Vegan",         "#166534"],
    ["vegetarian",  "Vegetarian",    "#16A34A"],
    ["pescatarian", "Pescatarian",   "#0891B2"],
    ["keto",        "Keto",          "#7C2D12"],
    ["paleo",       "Paleo",         "#854D0E"],
    ["gluten_free", "Gluten-Free",   "#B45309"],
    ["dairy_free",  "Dairy-Free",    "#374151"],
    ["nut_free",    "Nut-Free",      "#78350F"],
    ["soy_free",    "Soy-Free",      "#059669"],
    ["halal",       "Halal",         "#0369A1"],
    ["kosher",      "Kosher",        "#4338CA"],
    ["jain",        "Jain",          "#DC2626"],
    ["organic",     "Organic",       "#15803D"],
    ["non_gmo",     "Non-GMO",       "#0E7490"],
    ["low_carb",    "Low-Carb",      "#B91C1C"],
    ["high_protein","High-Protein",  "#7C3AED"],
    ["high_fiber",  "High-Fibre",    "#EA580C"],
  ].filter(([k]) => diet[k] === true);

  return (
    <section className="max-w-6xl mx-auto px-4 py-6" data-testid="food-item-identifiers">
      <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_auto]">
        {/* ── Left column: identifier card ─────────────────────── */}
        {hasIdentifierBlock && (
          <div className="rounded-2xl border bg-white p-4 sm:p-5 flex flex-col sm:flex-row gap-4 items-start"
               style={{ borderColor: "#DCEAD1" }} data-testid="fp-identifier-card">
            {/* QR badge */}
            {qrCodeUrl && (
              <div className="flex-none w-32 rounded-xl border p-2 text-center"
                   style={{ borderColor: "#B8D3A1", background: "#F7FBF4" }}>
                <img src={qrCodeUrl}
                     alt="QR code · scan or tap"
                     className="w-full aspect-square rounded"
                     data-testid="fp-qr-image" />
                <div className="text-[9px] font-mono mt-1 opacity-60">Scan or share</div>
              </div>
            )}

            <div className="flex-1 min-w-0 space-y-2">
              {/* Product name — link to the canonical product page so users
                  can confirm which item they're about to scan / share. */}
              {productName && (
                <div className="flex flex-wrap gap-2 items-baseline" data-testid="fp-identifier-product-name">
                  <span className="text-[10px] font-mono uppercase tracking-widest opacity-60">Product</span>
                  <Link
                    to={`/food-pulse/food/${slug}`}
                    className="font-display text-base sm:text-lg font-semibold hover:underline"
                    style={{ color: "#1A3B1A" }}
                    data-testid="fp-identifier-product-link"
                  >
                    {productName}
                  </Link>
                </div>
              )}
              {/* FP ID + short URL row */}
              <div className="flex flex-wrap gap-3 items-baseline">
                <span className="text-[10px] font-mono uppercase tracking-widest opacity-60">FoodPulse ID</span>
                <span className="font-mono text-sm font-semibold" style={{ color: "#1A3B1A" }}
                      data-testid="fp-product-id">{ids.foodpulse_product_id}</span>
              </div>
              {shortUrl && (
                <div className="flex flex-wrap gap-2 items-center">
                  <span className="text-[10px] font-mono uppercase tracking-widest opacity-60">Short URL</span>
                  <a href={shortUrl} target="_blank" rel="noreferrer"
                     className="text-xs font-mono underline"
                     style={{ color: "#2F6B2F" }}
                     data-testid="fp-short-url">{shortUrl}</a>
                  <button type="button" onClick={copyShortUrl}
                          className="text-xs inline-flex items-center gap-1 opacity-70 hover:opacity-100"
                          data-testid="fp-short-url-copy">
                    {copied ? <><Check className="w-3 h-3" /> copied</> : <><Copy className="w-3 h-3" /> copy</>}
                  </button>
                </div>
              )}

              {/* Barcode strip */}
              {barcodeUrl ? (
                <div className="mt-2 flex flex-wrap gap-3 items-center">
                  <img src={barcodeUrl}
                       alt={`Barcode · ${ids.gtin || ""}`}
                       className="rounded border" style={{ background: "#fff", borderColor: "#DCEAD1", maxHeight: 60 }}
                       data-testid="fp-barcode-image" />
                  <div className="text-[10px] font-mono uppercase tracking-widest opacity-60">
                    {ids.gtin_type} · <span className="opacity-80">{ids.gtin}</span>
                  </div>
                </div>
              ) : ids.plu ? (
                <div className="mt-2 inline-flex items-center gap-2 rounded-full px-3 py-1"
                     style={{ background: "#FFF7ED", color: "#B45309", border: "1px solid #FDBA74" }}
                     data-testid="fp-plu-badge">
                  <ScanLine className="w-3 h-3" />
                  <span className="text-[10px] font-mono uppercase tracking-widest">PLU · {ids.plu}</span>
                  {ids.plu_organic && <span className="text-[10px] opacity-70">· organic {ids.plu_organic}</span>}
                </div>
              ) : (
                <div className="mt-2 text-[10px] font-mono uppercase tracking-widest opacity-60">
                  No barcode on file · <Link to="/food-pulse/label-scan" className="underline" style={{ color: "#2F6B2F" }}>scan one in</Link>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Right column: dietary chips + certifications ────── */}
        {(hasDietary || hasCerts) && (
          <div className="space-y-3 sm:min-w-[220px] max-w-md">
            {hasDietary && (
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest opacity-60 mb-1.5">Dietary</div>
                <div className="flex flex-wrap gap-1.5" data-testid="fp-diet-chips">
                  {dietChips.map(([k, label, color]) => (
                    <span key={k}
                          className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-full border"
                          style={{ color, borderColor: `${color}55`, background: "#fff" }}
                          data-testid={`fp-diet-${k}`}>
                      {label}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {hasCerts && (
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest opacity-60 mb-1.5">Certifications</div>
                <div className="flex flex-wrap gap-1.5" data-testid="fp-cert-ribbons">
                  {ids.usda_organic && (
                    <span className="text-[10px] font-semibold px-2 py-1 rounded-full inline-flex items-center gap-1" style={{ background: "#DCFCE7", color: "#166534" }}>
                      <Award className="w-3 h-3" /> USDA Organic
                    </span>
                  )}
                  {ids.non_gmo_id && (
                    <span className="text-[10px] font-semibold px-2 py-1 rounded-full inline-flex items-center gap-1" style={{ background: "#F0FDFA", color: "#0E7490" }}>
                      <Award className="w-3 h-3" /> Non-GMO
                    </span>
                  )}
                  {ids.fair_trade_id && (
                    <span className="text-[10px] font-semibold px-2 py-1 rounded-full inline-flex items-center gap-1" style={{ background: "#FEF3C7", color: "#854D0E" }}>
                      <Award className="w-3 h-3" /> Fair Trade
                    </span>
                  )}
                  {ids.kosher_id && (
                    <span className="text-[10px] font-semibold px-2 py-1 rounded-full inline-flex items-center gap-1" style={{ background: "#EEF2FF", color: "#4338CA" }}>
                      <ShieldCheck className="w-3 h-3" /> Kosher
                    </span>
                  )}
                  {ids.halal_id && (
                    <span className="text-[10px] font-semibold px-2 py-1 rounded-full inline-flex items-center gap-1" style={{ background: "#EFF6FF", color: "#0369A1" }}>
                      <ShieldCheck className="w-3 h-3" /> Halal
                    </span>
                  )}
                  {ids.rainforest_alliance_id && (
                    <span className="text-[10px] font-semibold px-2 py-1 rounded-full inline-flex items-center gap-1" style={{ background: "#DCFCE7", color: "#166534" }}>
                      <Award className="w-3 h-3" /> Rainforest Alliance
                    </span>
                  )}
                  {certs.filter((c) => {
                    const l = String(c).toLowerCase();
                    return !l.includes("organic") && !l.includes("non-gmo") && !l.includes("fair trade") && !l.includes("kosher") && !l.includes("halal") && !l.includes("rainforest");
                  }).map((c, i) => (
                    <span key={i} className="text-[10px] font-semibold px-2 py-1 rounded-full inline-flex items-center gap-1"
                          style={{ background: "#F5F3FF", color: "#6D28D9" }}>
                      <Award className="w-3 h-3" /> {c}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* GHS Hazard card — full-width warning banner when present */}
      {ghs && (
        <div className="mt-4 rounded-2xl border-2 p-4 flex flex-col md:flex-row gap-3 items-start"
             style={{ borderColor: "#DC2626", background: "#FEF2F2" }} data-testid="fp-ghs-hazard-card">
          <div className="flex-none w-10 h-10 rounded-full inline-flex items-center justify-center"
               style={{ background: "#DC2626", color: "#fff" }}>
            <AlertTriangle className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display font-semibold text-base" style={{ color: "#7F1D1D" }}>
              {ghs.signal_word || "Warning"} · {ghs.classification || "GHS Hazard"}
            </div>
            {Array.isArray(ghs.hazard_statements) && ghs.hazard_statements.length > 0 && (
              <ul className="mt-1.5 text-sm space-y-0.5" style={{ color: "#7F1D1D" }}>
                {ghs.hazard_statements.map((h, i) => (<li key={i}>• {h}</li>))}
              </ul>
            )}
            {Array.isArray(ghs.precautionary_statements) && ghs.precautionary_statements.length > 0 && (
              <div className="mt-2">
                <div className="text-[10px] font-mono uppercase tracking-widest opacity-70">Precautions</div>
                <ul className="mt-0.5 text-xs space-y-0.5" style={{ color: "#7F1D1D" }}>
                  {ghs.precautionary_statements.map((p, i) => (<li key={i}>• {p}</li>))}
                </ul>
              </div>
            )}
            {Array.isArray(ghs.pictograms) && ghs.pictograms.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {ghs.pictograms.map((p, i) => (
                  <span key={i} className="text-[10px] font-mono px-2 py-0.5 rounded border"
                        style={{ borderColor: "#DC2626", color: "#7F1D1D", background: "#fff" }}>
                    {p}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
